/**
 * CartogramPanel - 카토그램 생성 패널
 */

import { layerManager } from '../../core/LayerManager.js';
import { cartogramTool } from '../../tools/CartogramTool.js';
import { eventBus, Events } from '../../utils/EventBus.js';
import { buildLayerOptions, resolveInitialLayerId } from '../../utils/layerSelect.js';

class CartogramPanel {
  constructor() {
    this.modalElement = null;
    this.selectedLayerId = null;
    this.selectedAttribute = null;
  }

  /**
   * 패널 표시
   */
  show() {
    const layers = layerManager.getAllLayers().filter(l =>
      l.type === 'vector' && l.geometryType !== 'Point'
    );

    if (layers.length === 0) {
      alert('카토그램을 생성하려면 폴리곤 레이어가 필요합니다.');
      return;
    }

    this.createModal(layers);
  }

  /**
   * 모달 생성
   */
  createModal(layers) {
    this.hide();

    // 선택 중인 레이어가 카토그램을 지원하면 미리 골라 준다.
    this.selectedLayerId = resolveInitialLayerId(layers, layerManager.getSelectedLayerId()) || null;
    this.selectedAttribute = null;

    const layerOptions = buildLayerOptions(layers, { selectedId: this.selectedLayerId });

    const modalHtml = `
      <div class="modal-overlay active" id="cartogram-modal">
        <div class="modal" style="width: 420px;">
          <div class="modal-header">
            <h3>카토그램 생성</h3>
            <button class="modal-close" id="cartogram-close">&times;</button>
          </div>
          <div class="modal-body" style="padding: var(--spacing-lg);">
            <div class="form-group">
              <label class="form-label">레이어 선택</label>
              <select class="form-select" id="cartogram-layer">${layerOptions}</select>
            </div>

            <div class="form-group">
              <label class="form-label">속성 선택 (크기 결정)</label>
              <select class="form-select" id="cartogram-attribute" disabled>
                <option value="">-- 먼저 레이어를 선택하세요 --</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">카토그램 유형</label>
              <div class="cartogram-type-options">
                <label class="cartogram-type-option">
                  <input type="radio" name="cartogram-type" value="dorling" checked>
                  <div class="type-content">
                    <div class="type-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40">
                        <circle cx="12" cy="12" r="8" fill="#4292c6" stroke="#333" stroke-width="1"/>
                        <circle cx="28" cy="15" r="12" fill="#2171b5" stroke="#333" stroke-width="1"/>
                        <circle cx="18" cy="30" r="6" fill="#6baed6" stroke="#333" stroke-width="1"/>
                      </svg>
                    </div>
                    <div class="type-info">
                      <strong>Dorling (원형)</strong>
                      <small>지역을 원으로 대체, 값에 따라 원 크기 조절</small>
                    </div>
                  </div>
                </label>
                <label class="cartogram-type-option">
                  <input type="radio" name="cartogram-type" value="contiguous">
                  <div class="type-content">
                    <div class="type-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40">
                        <polygon points="5,15 15,5 22,8 20,18 22,28 10,25" fill="#4292c6" stroke="#333" stroke-width="1"/>
                        <polygon points="22,8 32,6 35,20 25,28 20,18" fill="#2171b5" stroke="#333" stroke-width="1"/>
                        <polygon points="10,25 22,28 18,38 5,32" fill="#6baed6" stroke="#333" stroke-width="1"/>
                      </svg>
                    </div>
                    <div class="type-info">
                      <strong>Contiguous (연속)</strong>
                      <small>모양·인접 유지하며 면적을 값에 비례하게 변형 (Dougenik)</small>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">분류 방법</label>
              <select class="form-select" id="cartogram-method">
                <option value="quantile" selected>분위수 (Quantile)</option>
                <option value="naturalBreaks">자연 분류 (Jenks)</option>
                <option value="equalInterval">동일 간격</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">색상 스키마</label>
              <div class="color-scheme-options" id="color-schemes">
                <label class="color-scheme-option">
                  <input type="radio" name="color-scheme" value="blues" checked>
                  <div class="color-preview blues"></div>
                </label>
                <label class="color-scheme-option">
                  <input type="radio" name="color-scheme" value="reds">
                  <div class="color-preview reds"></div>
                </label>
                <label class="color-scheme-option">
                  <input type="radio" name="color-scheme" value="greens">
                  <div class="color-preview greens"></div>
                </label>
                <label class="color-scheme-option">
                  <input type="radio" name="color-scheme" value="oranges">
                  <div class="color-preview oranges"></div>
                </label>
                <label class="color-scheme-option">
                  <input type="radio" name="color-scheme" value="purples">
                  <div class="color-preview purples"></div>
                </label>
                <label class="color-scheme-option">
                  <input type="radio" name="color-scheme" value="spectral">
                  <div class="color-preview spectral"></div>
                </label>
              </div>
            </div>

          </div>
          <div class="modal-footer" style="padding: var(--spacing-md) var(--spacing-lg); border-top: 1px solid var(--border-color); display: flex; justify-content: flex-end; gap: var(--spacing-sm);">
            <button class="btn btn-secondary btn-sm" id="cartogram-cancel">취소</button>
            <button class="btn btn-primary btn-sm" id="cartogram-create" disabled>생성</button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
    this.modalElement = document.getElementById('cartogram-modal');

    this.bindEvents();
    this.addStyles();
    this.updateAttributeList();
  }

  /**
   * 이벤트 바인딩
   */
  bindEvents() {
    const closeBtn = document.getElementById('cartogram-close');
    const cancelBtn = document.getElementById('cartogram-cancel');
    const createBtn = document.getElementById('cartogram-create');
    const layerSelect = document.getElementById('cartogram-layer');
    const attributeSelect = document.getElementById('cartogram-attribute');

    closeBtn.addEventListener('click', () => this.hide());
    cancelBtn.addEventListener('click', () => this.hide());

    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) this.hide();
    });

    // 레이어 선택 시 속성 목록 업데이트
    layerSelect.addEventListener('change', (e) => {
      this.selectedLayerId = e.target.value || null;
      this.updateAttributeList();
    });

    // 속성 선택 시 생성 버튼 활성화
    attributeSelect.addEventListener('change', (e) => {
      this.selectedAttribute = e.target.value;
      createBtn.disabled = !this.selectedAttribute;
    });

    // 카토그램 생성
    createBtn.addEventListener('click', () => this.createCartogram());
  }

  /**
   * 속성 목록 업데이트
   */
  updateAttributeList() {
    const attributeSelect = document.getElementById('cartogram-attribute');
    const createBtn = document.getElementById('cartogram-create');

    if (!this.selectedLayerId) {
      attributeSelect.innerHTML = '<option value="">-- 먼저 레이어를 선택하세요 --</option>';
      attributeSelect.disabled = true;
      createBtn.disabled = true;
      return;
    }

    const attributes = cartogramTool.getNumericAttributes(this.selectedLayerId);

    if (attributes.length === 0) {
      attributeSelect.innerHTML = '<option value="">숫자 속성이 없습니다</option>';
      attributeSelect.disabled = true;
      createBtn.disabled = true;
      return;
    }

    const options = attributes.map(attr =>
      `<option value="${attr}">${attr}</option>`
    );

    attributeSelect.innerHTML = '<option value="">-- 속성 선택 --</option>' + options.join('');
    attributeSelect.disabled = false;
    this.selectedAttribute = null;
    createBtn.disabled = true;
  }

  /**
   * 카토그램 생성
   */
  createCartogram() {
    if (!this.selectedLayerId || !this.selectedAttribute) {
      alert('레이어와 속성을 선택해주세요.');
      return;
    }

    const cartogramType = document.querySelector('input[name="cartogram-type"]:checked').value;
    const colorScheme = document.querySelector('input[name="color-scheme"]:checked').value;
    const method = document.getElementById('cartogram-method').value;
    // 라벨은 '레이어 > 라벨 설정'이 담당한다 (필드·글꼴·위치까지 다룬다)
    const opts = { colorScheme, method };

    try {
      let newLayerId;

      if (cartogramType === 'dorling') {
        newLayerId = cartogramTool.createDorlingCartogram(
          this.selectedLayerId, this.selectedAttribute, opts
        );
      } else {
        newLayerId = cartogramTool.createContiguousCartogram(
          this.selectedLayerId, this.selectedAttribute, opts
        );
      }

      this.hide();

      // 레이어 삭제 시 범례도 제거
      const removeHandler = (data) => {
        if (data && data.layerId === newLayerId) {
          cartogramTool.removeLegend(newLayerId);
          eventBus.off(Events.LAYER_REMOVED, removeHandler);
          eventBus.off(Events.LAYER_VISIBILITY_CHANGED, visibilityHandler);
        }
      };
      eventBus.on(Events.LAYER_REMOVED, removeHandler);

      // 레이어 가시성 변경 시 범례도 숨김/표시
      const visibilityHandler = (data) => {
        if (data && data.layerId === newLayerId) {
          const legend = document.getElementById(`legend-${newLayerId}`);
          if (legend) {
            legend.style.display = data.visible ? 'block' : 'none';
          }
        }
      };
      eventBus.on(Events.LAYER_VISIBILITY_CHANGED, visibilityHandler);

    } catch (error) {
      alert('카토그램 생성 실패: ' + error.message);
    }
  }

  /**
   * 스타일 추가
   */
  addStyles() {
    if (document.getElementById('cartogram-panel-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'cartogram-panel-styles';
    styles.textContent = `
      .cartogram-type-options {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .cartogram-type-option {
        display: flex;
        align-items: flex-start;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .cartogram-type-option:hover {
        background: var(--bg-hover);
      }

      .cartogram-type-option input[type="radio"] {
        margin-top: 12px;
      }

      .cartogram-type-option input[type="radio"]:checked + .type-content {
        opacity: 1;
      }

      .cartogram-type-option input[type="radio"]:checked ~ .type-content .type-info strong {
        color: var(--color-primary);
      }

      .cartogram-type-option:has(input:checked) {
        border-color: var(--color-primary);
        background: var(--bg-selected);
      }

      .type-content {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        opacity: 0.7;
      }

      .type-icon {
        flex-shrink: 0;
      }

      .type-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .type-info strong {
        font-size: var(--font-size-sm);
      }

      .type-info small {
        font-size: var(--font-size-xs);
        color: var(--text-secondary);
      }

      .color-scheme-options {
        display: flex;
        gap: var(--spacing-sm);
        flex-wrap: wrap;
      }

      .color-scheme-option {
        cursor: pointer;
      }

      .color-scheme-option input[type="radio"] {
        display: none;
      }

      .color-scheme-option .color-preview {
        width: 50px;
        height: 20px;
        border-radius: var(--radius-sm);
        border: 2px solid transparent;
        transition: border-color var(--transition-fast);
      }

      .color-scheme-option input[type="radio"]:checked + .color-preview {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 2px var(--color-primary-light);
      }

      .color-preview.blues {
        background: linear-gradient(to right, #f7fbff, #deebf7, #9ecae1, #4292c6, #084594);
      }

      .color-preview.reds {
        background: linear-gradient(to right, #fff5f0, #fee0d2, #fc9272, #ef3b2c, #99000d);
      }

      .color-preview.greens {
        background: linear-gradient(to right, #f7fcf5, #e5f5e0, #a1d99b, #41ab5d, #005a32);
      }

      .color-preview.oranges {
        background: linear-gradient(to right, #fff5eb, #fee6ce, #fdae6b, #f16913, #8c2d04);
      }

      .color-preview.purples {
        background: linear-gradient(to right, #fcfbfd, #efedf5, #bcbddc, #807dba, #4a1486);
      }

      .color-preview.spectral {
        background: linear-gradient(to right, #3288bd, #66c2a5, #e6f598, #fdae61, #d53e4f);
      }

      .checkbox-label {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        cursor: pointer;
        font-size: var(--font-size-sm);
      }
    `;
    document.head.appendChild(styles);
  }

  /**
   * 모달 숨기기
   */
  hide() {
    if (this.modalElement) {
      this.modalElement.classList.remove('active');
      setTimeout(() => {
        this.modalElement?.remove();
        this.modalElement = null;
      }, 200);
    }
  }
}

export const cartogramPanel = new CartogramPanel();
